import "react-calendar/dist/Calendar.css";
import { CardComp, TableCompData } from "../../../components";
import { AttendanceHistoryHeader } from "../../../utils/TableHeader";
import FilterFind from "../../../components/FilterFind";
import EmptyTable from "../../../components/Common/EmptyTable";
import { useGetMarkedMembersAttendanceQuery } from "../../../store/admin/attendanceSlice";
import { useMemo, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import TablePagination from "../../../components/Table/Pagination";
import AttendanceFilter from "./modules/AttendanceFilter";
import { status_icon } from "./modules/AttendanceFilter";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import AttendanceStatusChangeModal from "./modules/AttendanceStatusChangeModal";
import { useGetFinesQuery } from "../../../store/admin/settings/fineSlice";
import { resolveAbsenceFine } from "../../../utils/fineUtils";

export default function AttendanceHistory() {
  const [pageNumber, setPageNumber] = useState(1);
  const navigate = useNavigate();
  const location = useLocation();
  const perPage = 20;
  const [searchTerm, setSearchTerm] = useState("");
  
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);

  const { data: finesDataResponse } = useGetFinesQuery();

  // Extract fines array safely
  const finesList = useMemo(() => {
    if (Array.isArray(finesDataResponse)) return finesDataResponse;
    if (finesDataResponse?.data && Array.isArray(finesDataResponse.data)) return finesDataResponse.data;
    if (finesDataResponse?.fines && Array.isArray(finesDataResponse.fines)) return finesDataResponse.fines;
    return [];
  }, [finesDataResponse]);

  // Resolve absence/attendance fine (category "Attendance" or "Absence", or name fallback; active only)
  const { amount: attendanceFineAmount } = useMemo(
    () => resolveAbsenceFine(finesList),
    [finesList]
  );

  const handleEditEntry = (entry, member) => {
    // Add necessary status info if missing from the small history badge
    setSelectedEntry({
      ...entry,
      status_id: entry.attendance_status_id,
      status_name: entry.status || entry.status_name,
    });
    setSelectedMember(member);
    setShowEditModal(true);
  };
  
  // Get current date for default year and month
  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  
  const {
    data: markedAttendance = {},
    error,
    isFetching,
    isLoading,
  } = useGetMarkedMembersAttendanceQuery({
    page: pageNumber,
    limit: perPage,
    year: selectedYear,
    month: selectedMonth,
  });

  const { company_attendances, pagination } = useMemo(() => {
    if (!markedAttendance) {
      return { company_attendances: [], pagination: {} };
    }
    
    // Handle different possible response structures
    let attendanceList = [];
    if (Array.isArray(markedAttendance)) {
      attendanceList = markedAttendance;
    } else if (markedAttendance?.company_attendances) {
      attendanceList = markedAttendance.company_attendances;
    } else if (markedAttendance?.data?.company_attendances) {
      attendanceList = markedAttendance.data.company_attendances;
    } else if (markedAttendance?.data && Array.isArray(markedAttendance.data)) {
      attendanceList = markedAttendance.data;
    }
    
    const { pagination = {} } = markedAttendance;
    
    // Map server data into table rows
    const itemsMapped = attendanceList.map((attendance, index) => {
      const { attendanceStatus = [] } = attendance;
      const status = attendanceStatus.map((sta) => {
        const [day, , dt] = new Date(sta?.created_at || sta?.attendance_date || sta?.date).toDateString().split(" ");
        return {
          ...sta,
          icon: status_icon[sta?.attendance_status_id],
          week_day: day,
          date: dt,
        };
      });
      return {
        ...attendance,
        serialNumber:
          index + 1 + (pageNumber - 1) * (pagination.perPage || perPage),
        attendanceStatus: status,
        viewAll: () => {
          navigate(
            `${location.pathname}/${attendance?.member?.id || attendance?.member_id}?year=${selectedYear}&month=${selectedMonth}`
          );
        },
      };
    });

    // Client-side filter by member name
    const term = (searchTerm || "").toLowerCase();
    const items = term
      ? itemsMapped.filter((row) => {
          const name = `${row?.member?.first_name || ""} ${row?.member?.last_name || ""}`.toLowerCase();
          return name.includes(term);
        })
      : itemsMapped;
    return {
      pagination,
      company_attendances: items,
    };
  }, [markedAttendance, pageNumber, location.pathname, navigate, perPage, searchTerm, selectedYear, selectedMonth]);
  
  useEffect(() => {
    if (error?.status === 401) {
      navigate("/login");
    } else if (error) {
      console.error("Attendance history error:", error);
    }
  }, [error, navigate]);
  return (
    <section className="mx-lg-4 min-vh-100 pb-5 mb-5">
      <FilterFind 
        title={"Attendance History"}
        searchValue={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
      />
      
      <AttendanceFilter 
        onDateChange={(year, month) => {
          setSelectedYear(year);
          setSelectedMonth(month);
          setPageNumber(1); // Reset to first page when date changes
        }}
      />

      <CardComp cardClassName="shadow-sm border-0 px-0">
        <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center bg-light rounded-top">
          <div>
            <h5 className="mb-0 fw-bold text-black1" style={{ fontSize: "16px" }}>Monthly Attendance Summary</h5>
            <p className="text-muted mb-0 small">Overview of all members' attendance for the selected period</p>
          </div>
          <div className="d-flex gap-3">
             {/* Add any bulk actions here if needed */}
          </div>
        </div>
        <div className="px-0 pb-0">
          <TableCompData
            marginBottom={"0px"}
            progressPending={isFetching || isLoading}
            progressComponent={<TableSkeletonLoader rows={10} columns={6} />}
            message={
              !isFetching && !isLoading && company_attendances.length === 0 ? (
                <EmptyTable
                  Message={"No attendance records found for the selected period"}
                  btnText={"Mark attendance"}
                  onClick={() => navigate("/super_admin/attendance")}
                />
              ) : null
            }
            columns={AttendanceHistoryHeader(false, handleEditEntry)}
            data={company_attendances}
          />
        </div>
      </CardComp>
      {!isFetching && !isLoading && company_attendances.length > 0 && (
        <div className="bg-white rounded shadow-sm border-0 py-4 mt-4 px-4">
          <TablePagination
            marginTop={"0"}
            handlePageChange={setPageNumber}
            count={company_attendances.length}
            currentPage={pagination?.currentPage || 1}
            perPage={pagination?.perPage || perPage}
            total={pagination?.total || company_attendances.length}
            totalPages={pagination?.lastPage || 1}
          />
        </div>
      )}

      {/* Edit Attendance Modal */}
      {showEditModal && selectedEntry && selectedMember && (
        <AttendanceStatusChangeModal
          show={showEditModal}
          handleClose={() => setShowEditModal(false)}
          memberData={{
            name: `${selectedMember.first_name} ${selectedMember.last_name}`,
            member_no: selectedMember.member_no,
          }}
          currentStatus={{
            id: selectedEntry.status_id,
            name: selectedEntry.status_name,
          }}
          fineData={selectedEntry.fine || { amount: attendanceFineAmount, status: "PAID" }}
        />
      )}
    </section>
  );
}

